import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

/**
 * ALIŞ FATURALARI — panel istemcisi.
 *
 * ⚠ BURADAKİ HİÇBİR LİSTE BİR KAPI DEĞİLDİR. Sunucu `purchases.dto.ts` +
 * `purchases.service.ts` + DB kısıtlarıyla kendi kapısını kurar; buradakiler
 * operatöre neyi seçtiğini ANLATMAK içindir. Hukuki bir kuralı panelde de
 * tanımlamak, iki listenin ayrışacağı bir yüzey açar.
 *
 * ⚠ Diziler `apps/api/src/modules/purchases/*.ts` ile BİREBİR aynı olmak
 * zorunda. Paylaşılan bir pakete taşımak ayrı bir iş; bugün panel API'nin
 * `dist`ini import edemiyor.
 */

export const PURCHASE_NATURES = [
  'GOODS_FOR_RESALE',
  'SERVICES_AND_OTHER_GOODS',
  'INVESTMENT',
] as const;
export type PurchaseNature = (typeof PURCHASE_NATURES)[number];

export const PURCHASE_REGIMES = [
  'DOMESTIC',
  'INTRA_EU_GOODS',
  'INTRA_EU_SERVICES',
  'REVERSE_CHARGE_OTHER',
] as const;
export type PurchaseRegime = (typeof PURCHASE_REGIMES)[number];

export const VAT_RESTRICTED_CATEGORIES = [
  'MANUFACTURED_TOBACCO',
  'SPIRITUOUS_BEVERAGES',
  'ACCOMMODATION_FOOD_BEVERAGE',
  'RECEPTION_COSTS',
  'ROAD_MOTOR_VEHICLE',
] as const;
export type VatRestrictedCategory = (typeof VAT_RESTRICTED_CATEGORIES)[number];

export type PurchaseStatus = 'DRAFT' | 'CONFIRMED';

/**
 * ⚠ ETİKETLER KUTU TANIMINI TAŞIR, günlük dili değil. "Mal" demek, satın alınan
 * her fiziksel nesneyi kutu 81'e sürükler; oysa 81 yeniden SATILACAK maldır ve
 * bir BT işletmesinde neredeyse hep boştur — alınan klavye kutu 82'ye aittir.
 */
export const NATURE_LABELS: Record<PurchaseNature, string> = {
  GOODS_FOR_RESALE: 'Yeniden satılacak mal / hammadde (kutu 81)',
  SERVICES_AND_OTHER_GOODS: 'Hizmet ve çeşitli mal alımı (kutu 82)',
  INVESTMENT: 'Yatırım — duran varlık (kutu 83)',
};

/**
 * ⚠ REJİM TEDARİKÇİNİN ÜLKESİNDEN TÜRETİLMEZ. Md. 51 §2, 5°'in şartları
 * arasında "md. 39–44bis uyarınca muaf değil" de var ve bu veriden okunamaz;
 * nitelemeyi operatör yapar.
 */
export const REGIME_LABELS: Record<PurchaseRegime, string> = {
  DOMESTIC: 'Yurtiçi — vergiyi tedarikçi borçlanır',
  INTRA_EU_GOODS: 'AB içi MAL edinimi (kutu 86)',
  INTRA_EU_SERVICES: 'AB tedarikçiden HİZMET — ters yükümlülük (kutu 88)',
  REVERSE_CHARGE_OTHER: 'Belçika dışı tedarikçi — ters yükümlülük (kutu 87)',
};

/**
 * ⚠ ÖLÇÜT AB ÜYELİĞİ DEĞİL, YERLEŞİKLİK. Md. 51 §2, 5° "un assujetti qui n'est
 * pas établi en Belgique" diyor — ABD'li bir SaaS sağlayıcısı da bu dala girer.
 */
export const REGIME_HINTS: Record<PurchaseRegime, string> = {
  DOMESTIC: 'Belçikalı tedarikçi KDV’yi faturasında gösterir ve devlete o öder.',
  INTRA_EU_GOODS: 'AB’den mal aldığınızda vergi zaten SİZDE doğar (md. 51 §1, 2°).',
  INTRA_EU_SERVICES:
    'AB’li tedarikçi KDV’siz fatura keser; vergiyi siz beyan edip aynı anda indirirsiniz.',
  REVERSE_CHARGE_OTHER:
    'Ölçüt AB üyeliği DEĞİL, tedarikçinin Belçika’da yerleşik olmamasıdır — ABD’li bir SaaS sağlayıcısı da buraya girer (md. 51 §2, 5°).',
};

/**
 * ⚠ BOŞ BIRAKMAK GEÇERLİ BİR CEVAPTIR. Harcamaların çoğu (hosting, yazılım,
 * ofis) hiçbir kısıta girmez; her satırda kategori seçtirmek asıl kısıtlı
 * kalemleri sıradanlaştırırdı.
 */
export const RESTRICTION_LABELS: Record<VatRestrictedCategory, string> = {
  MANUFACTURED_TOBACCO: 'İşlenmiş tütün — KDV %0 (md. 45 §3, 1°)',
  SPIRITUOUS_BEVERAGES: 'Yüksek alkollü içki — KDV %0 (md. 45 §3, 2°)',
  ACCOMMODATION_FOOD_BEVERAGE: 'Konaklama / yiyecek / içecek — KDV %0 (md. 45 §3, 3°)',
  RECEPTION_COSTS: 'Temsil ve ağırlama — KDV %0 (md. 45 §3, 4°)',
  ROAD_MOTOR_VEHICLE: 'Karayolu taşıtı — KDV en çok %50 (md. 45 §2)',
};

/** Hükmün dayattığı ÜST SINIR. Operatör aşağı inebilir, yukarı çıkamaz. */
export const RESTRICTION_CAPS: Record<VatRestrictedCategory, number> = {
  MANUFACTURED_TOBACCO: 0,
  SPIRITUOUS_BEVERAGES: 0,
  ACCOMMODATION_FOOD_BEVERAGE: 0,
  RECEPTION_COSTS: 0,
  ROAD_MOTOR_VEHICLE: 50,
};

/**
 * Tedarikçi KDV'siz keser, vergiyi alıcı beyan eder. Panel bunu yalnız
 * ETİKET seçmek için kullanır (tutar hesaplamaz; öneri sunucudan gelir).
 */
export function isReverseChargeRegime(regime: PurchaseRegime): boolean {
  return regime !== 'DOMESTIC';
}

export const STATUS_LABELS: Record<PurchaseStatus, string> = {
  DRAFT: 'Taslak',
  CONFIRMED: 'Onaylandı',
};

export interface PurchaseLine {
  id?: string;
  lineNo?: number;
  description: string;
  baseExclVat: string;
  vatRate: string;
  purchaseNature: PurchaseNature;
  regime: PurchaseRegime;
  vatDeductiblePct?: string;
  incomeTaxDeductiblePct?: string;
  privateUsePct?: string;
  vatRestrictionCategory?: VatRestrictedCategory | null;
  expenseAccountCode?: string | null;
}

export interface PurchaseInvoice {
  id: string;
  documentType: 'INVOICE' | 'CREDIT_NOTE';
  status: PurchaseStatus;
  source: string;
  supplierId: string | null;
  supplierName: string;
  supplierVatNumber: string | null;
  supplierCountry: string;
  supplierDocumentNumber: string;
  invoiceDate: string;
  supplyDate: string | null;
  dueDate: string | null;
  /**
   * KDV'nin muaccel olduğu gün — belgenin KDV DÖNEMİNİ bu alan belirler.
   *
   * ⚠ Onayda `vatPeriodKey`e dönüşüp DONAR; boşsa `invoiceDate` kullanılır.
   */
  vatChargeableAt: string | null;
  /** '2026Q3' — onayda donar; taslakta `null`. */
  vatPeriodKey: string | null;
  currency: string;
  totalExclVat: string;
  totalVat: string;
  totalInclVat: string;
  deductibleVat: string;
  nonDeductibleVat: string;
  vatBreakdown: Array<{ grid: string; amount: string }> | null;
  notes: string | null;
  confirmedAt: string | null;
  /**
   * SATIRLARDAN BEKLENEN KDV — belgede yazanın YERİNE GEÇMEZ.
   *
   * ⚠ Onay alanını önceden doldurmak ve ayrışmayı söylemek içindir: 10,50
   * yerine 105,00 yazmak kutu 59'u sessizce on katına çıkarıyordu.
   *
   * ⚠⚠ TERS YÜKÜMLÜLÜKTE SIFIR DEĞİL, BEYAN EDİLECEK KDV'DİR (matrah × oran).
   * Tedarikçi KDV'siz keser ama onay alanı BİZİM beyan ettiğimiz vergiyi
   * taşır; bir süre burada sıfır önerildi ve onaylanan AB hizmet alımları
   * kutu 55/59'u boş bıraktı.
   */
  suggestedVat?: string;
  /** Tedarikçi belgesinin arşivdeki eki — bir kez yazılır, değiştirilemez. */
  attachmentStoragePath: string | null;
  attachmentContentType: string | null;
  attachmentByteLength: number | null;
  lines?: PurchaseLine[];
}

export interface ListPurchasesParams {
  page?: number;
  pageSize?: number;
  status?: PurchaseStatus;
  q?: string;
}

export function usePurchases(params: ListPurchasesParams) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.status) qs.set('status', params.status);
  if (params.q) qs.set('q', params.q);

  return useQuery({
    queryKey: ['purchases', params],
    queryFn: () =>
      api.get<{ items: PurchaseInvoice[]; total: number }>(`/purchases?${qs.toString()}`),
  });
}

export function usePurchase(id: string | undefined) {
  return useQuery({
    queryKey: ['purchase', id],
    queryFn: () => api.get<PurchaseInvoice>(`/purchases/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreatePurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.post<PurchaseInvoice>('/purchases', body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchases'] }),
  });
}

export function useUpdatePurchase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: unknown) => api.patch<PurchaseInvoice>(`/purchases/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      void qc.invalidateQueries({ queryKey: ['purchase', id] });
    },
  });
}

export function useConfirmPurchase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentVat: string) =>
      api.post<PurchaseInvoice>(`/purchases/${id}/confirm`, { documentVat }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      void qc.invalidateQueries({ queryKey: ['purchase', id] });
    },
  });
}

export function useReopenPurchase(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<PurchaseInvoice>(`/purchases/${id}/reopen`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      void qc.invalidateQueries({ queryKey: ['purchase', id] });
    },
  });
}

/**
 * TASLAK belgeyi siler.
 *
 * ⚠ YALNIZ TASLAK. Onaylanmış belge silinmez (sunucu reddeder): kutuları
 * donmuştur ve beyana girmiş olabilir. Yanlış girilen bir taslağın silinebilir
 * olması ise şart — aksi hâlde listede sonsuza kadar duran, onaylanamayan ve
 * düzeltilemeyen bir kayıt kalıyordu.
 */
export function useDeletePurchase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/purchases/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['purchases'] }),
  });
}

/**
 * Tedarikçi belgesinin PDF/görüntüsünü yükler.
 *
 * ⚠ GÖVDE HAM BAYTLARDIR, `FormData` DEĞİL. Sunucu `Content-Type`a güvenmez,
 * ilk baytlarla karşılaştırır. Ek bir kez yazılır.
 */
export function useUploadPurchaseAttachment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) =>
      api.postRaw<PurchaseInvoice>(`/purchases/${id}/attachment`, file, file.type),
    onSuccess: (_res, vars) => {
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      void qc.invalidateQueries({ queryKey: ['purchase', vars.id] });
    },
  });
}

/** Arşivdeki eki indirir — sunucu özeti doğrulamadan vermez. */
export function downloadPurchaseAttachment(id: string) {
  return api.file(`/purchases/${id}/attachment`);
}

/**
 * Gelen belgeden TASLAK alış üretir.
 *
 * ⚠ TASLAK, ONAY DEĞİL: tutarlar tedarikçinin XML'inden okunur ama indirim
 * oranları ve kutu kararı operatörün onayıyla donar. Köprü yalnız yazmayı
 * kolaylaştırır, kararı almaz.
 */
export function useCreatePurchaseFromIncoming() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (incomingId: string) =>
      api.post<PurchaseInvoice>(`/purchases/from-incoming/${incomingId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['purchases'] });
      void qc.invalidateQueries({ queryKey: ['einvoice', 'inbox'] });
    },
  });
}
